import { useContext, useEffect, useMemo, useState } from "react";
import { CircularProgress, Typography } from "@mui/material";
import CMForm from "./CMForm";
import CMVisibilitySections from "./CMVisibilitySections";
import {
  fetchCategoryMonitoringOptions,
  fetchCategoryMonitoringDetail,
  fetchCategoryMonitoringConsensus,
} from "./category-monitoring-api";
import { AuthContext } from "../../context/auth-context";
import classes from "./CategoryMonitoring.module.css";

export default function CategoryMonitoring() {
  const { selectedCustomerId } = useContext(AuthContext);

  const [optionsByCategory, setOptionsByCategory] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [category, setCategory] = useState("");
  const [llmMode, setLLMMode] = useState("single"); // "single" | "all"
  const [singleLlm, setSingleLlm] = useState("");
  const [market, setMarket] = useState("");
  const [rawDatasetLlm, setRawDatasetLlm] = useState("");

  const [consensusData, setConsensusData] = useState(null);
  const [consensusLoading, setConsensusLoading] = useState(false);

  const [rawData, setRawData] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);

  const categoryOptions = optionsByCategory.map((entry) => ({
    name: entry.category,
    value: entry.category,
  }));

  const activeCategoryEntry = optionsByCategory.find((entry) => entry.category === category);
  const llmOptions = (activeCategoryEntry?.llms ?? []).map((l) => ({
    name: l.display_name || l.name,
    value: l.id,
  }));
  const marketOptions = (activeCategoryEntry?.markets ?? []).map((m) => ({
    name: m.name,
    value: m.id,
  }));

  const llms = useMemo(
    () =>
      llmMode === "all"
        ? llmOptions.map((o) => o.value)
        : singleLlm != null && singleLlm !== ""
        ? [singleLlm]
        : [],
    [llmMode, singleLlm, activeCategoryEntry]
  );

  useEffect(() => {
    if (!selectedCustomerId) return;

    (async () => {
      setOptionsLoading(true);
      try {
        const options = await fetchCategoryMonitoringOptions(selectedCustomerId);
        setOptionsByCategory(options);
        setCategory(options[0]?.category ?? "");
      } finally {
        setOptionsLoading(false);
      }
    })();
  }, [selectedCustomerId]);

  // Whenever the category changes, default to Single mode on its first LLM/Market.
  useEffect(() => {
    if (!category) {
      setLLMMode("single");
      setSingleLlm("");
      setMarket("");
      return;
    }
    const entry = optionsByCategory.find((e) => e.category === category);
    setLLMMode("single");
    setSingleLlm(entry?.llms?.[0]?.id ?? "");
    setMarket(entry?.markets?.[0]?.id ?? "");
  }, [category]);

  useEffect(() => {
    if (!llms.length) {
      setRawDatasetLlm("");
      return;
    }
    if (!llms.includes(rawDatasetLlm)) {
      setRawDatasetLlm(llms[0]);
    }
  }, [llms]);

  useEffect(() => {
    if (!selectedCustomerId || !category || !market || llmMode !== "all") return;

    let cancelled = false;
    (async () => {
      setConsensusLoading(true);
      try {
        const consensus = await fetchCategoryMonitoringConsensus({
          customerId: selectedCustomerId,
          category,
          marketId: market,
        });
        if (!cancelled) setConsensusData(consensus);
      } finally {
        if (!cancelled) setConsensusLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, category, market, llmMode, refreshTick]);

  // Raw Datasets: one LLM's own detail (sets/attribute breakdown + brand domains).
  useEffect(() => {
    if (!selectedCustomerId || !category || !market || !rawDatasetLlm) return;

    let cancelled = false;
    (async () => {
      setRawLoading(true);
      try {
        const detail = await fetchCategoryMonitoringDetail({
          customerId: selectedCustomerId,
          category,
          llmId: rawDatasetLlm,
          marketId: market,
        });
        if (!cancelled) setRawData(detail?.json_response ?? null);
      } finally {
        if (!cancelled) setRawLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, category, market, rawDatasetLlm, refreshTick]);

  const loading = consensusLoading || rawLoading;
  const hasAnyData = Boolean(consensusData || rawData);
  const showFullPageLoader = optionsLoading || (loading && !hasAnyData);

  return (
    <div className={classes.page}>
      <CMForm
        category={category}
        llmMode={llmMode}
        singleLlm={singleLlm}
        market={market}
        categoryOptions={categoryOptions}
        llmOptions={llmOptions}
        marketOptions={marketOptions}
        onCategoryChange={setCategory}
        onLLMModeChange={setLLMMode}
        onSingleLLMChange={setSingleLlm}
        onMarketChange={setMarket}
        onRefresh={() => setRefreshTick((t) => t + 1)}
        loading={loading || optionsLoading}
        optionsLoading={optionsLoading}
      />

      {showFullPageLoader ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={36} thickness={4} />
          <Typography variant="body2" sx={{ mt: 1.5, color: "inherit" }}>
            Loading category monitoring data...
          </Typography>
        </div>
      ) : (
        hasAnyData && (
          <CMVisibilitySections
            llmMode={llmMode}
            consensusData={consensusData}
            consensusLoading={consensusLoading}
            rawData={rawData}
            rawLoading={rawLoading}
            llmOptions={llmOptions.filter((o) => llms.includes(o.value))}
            rawDatasetLlm={rawDatasetLlm}
            onRawDatasetLlmChange={setRawDatasetLlm}
          />
        )
      )}
    </div>
  );
}
